import { SCHEDULED_NOTIFICATION_WINDOW } from '@/resources/constants/orders'
import { DeliveryType, OrderStatus } from '@/resources/enums'
import { NotificationType } from '@/resources/enums/orders'
import { ActiveOrder, Delivery } from '@/resources/types'
import {
  ScheduledNotificationRange,
  ScheduledNotificationType,
} from '@/resources/types/notification'
import { addTimeZone } from '@/resources/utils'
import { MutableRefObject } from 'react'

export const getNotificationType = (hasTurbo: boolean) =>
  hasTurbo ? NotificationType.TURBO : NotificationType.DEFAULT

const isPendingOrder = (status: OrderStatus | string) =>
  status === (OrderStatus.PENDING as string) ||
  status === (OrderStatus.TRANSFERED as string)

const isTurboOrder = (delivery: Delivery) =>
  delivery?.type === DeliveryType.TURBO

export const getPendingOrderNumbers = (orders: ActiveOrder[]) => {
  const pendingOrders =
    orders?.filter(({ status }) => isPendingOrder(status)) ?? []
  return pendingOrders.map(({ number }) => number)
}

const getNewPendingOrders = (
  currentOrders: string[],
  previousOrders: string[]
) => currentOrders.filter(orderNumber => !previousOrders.includes(orderNumber))

export const validatePendingOrders = (
  orders: ActiveOrder[],
  prevPendingOrderNumbers: MutableRefObject<string[]>
) => {
  const pendingOrderNumbers = getPendingOrderNumbers(orders)
  const newOrderNumbers = getNewPendingOrders(
    pendingOrderNumbers,
    prevPendingOrderNumbers.current
  )

  const hasNewPendingOrders = newOrderNumbers.length > 0

  const newOrders =
    orders?.filter(({ number }) => newOrderNumbers.includes(number)) ?? []
  const hasNewTurboOrders = newOrders.some(({ delivery }) =>
    isTurboOrder(delivery)
  )

  prevPendingOrderNumbers.current = pendingOrderNumbers

  return { hasNewPendingOrders, hasNewTurboOrders }
}

export const validateNewOrderNumbersNotification = (
  currentNumbers: string[],
  prevSnapshotRef: MutableRefObject<string[]>
): { hasNewOrders: boolean } => {
  const prev = prevSnapshotRef.current
  const hasNewOrders = currentNumbers.some(n => !prev.includes(n))
  prevSnapshotRef.current = [...currentNumbers]
  return { hasNewOrders }
}

export type SellerNewOrderNotificationEntry = {
  orderNumber: string
  deliveryType: string
}

const isTurboDeliveryTypeFromNotification = (deliveryType: string): boolean =>
  deliveryType.toUpperCase() === (DeliveryType.TURBO as string)

export const hasTurboInSellerNewOrderNotifications = (
  entries: SellerNewOrderNotificationEntry[]
): boolean =>
  entries.some(({ deliveryType }) =>
    isTurboDeliveryTypeFromNotification(deliveryType)
  )

export const validateNewOrderNotifications = (
  current: SellerNewOrderNotificationEntry[],
  prevOrderNumbersSnapshotRef: MutableRefObject<string[]>
): { hasNewOrders: boolean; hasNewTurboAmongNew: boolean } => {
  const prev = prevOrderNumbersSnapshotRef.current
  const newlyAppeared = current.filter(
    ({ orderNumber }) => !prev.includes(orderNumber)
  )
  const hasNewOrders = newlyAppeared.length > 0
  const hasNewTurboAmongNew = newlyAppeared.some(({ deliveryType }) =>
    isTurboDeliveryTypeFromNotification(deliveryType)
  )
  prevOrderNumbersSnapshotRef.current = current.map(({ orderNumber }) =>
    String(orderNumber)
  )
  return { hasNewOrders, hasNewTurboAmongNew }
}

export const hasPendingOrders = (orders: ActiveOrder[]) => {
  const pendingOrders =
    orders?.filter(({ status }) => isPendingOrder(status)) ?? []

  const hasPending = pendingOrders.length > 0
  const hasTurbo = pendingOrders.some(({ delivery }) => isTurboOrder(delivery))

  return { hasPending, hasTurbo }
}

const getTimeRangeFromStartDate = (
  timeDiff: number
): ScheduledNotificationRange | null => {
  if (timeDiff >= SCHEDULED_NOTIFICATION_WINDOW.WINDOW_1H30M) {
    return ScheduledNotificationRange.ONE_HOUR_THIRTY_MIN_AFTER_START
  }
  if (timeDiff >= SCHEDULED_NOTIFICATION_WINDOW.WINDOW_1H) {
    return ScheduledNotificationRange.ONE_HOUR_AFTER_START
  }
  if (timeDiff >= SCHEDULED_NOTIFICATION_WINDOW.WINDOW_30M) {
    return ScheduledNotificationRange.THIRTY_MIN_AFTER_START
  }
  if (
    timeDiff < 0 &&
    timeDiff >= -SCHEDULED_NOTIFICATION_WINDOW.BEFORE_DELIVERY
  ) {
    return ScheduledNotificationRange.TEN_MIN_BEFORE_START
  }
  return null
}

export const SCHEDULED_NOTIFICATION_KEY = 'SCHEDULED_ORDERS_NOTIFICATION'

const getScheduledNotificationTracking = () => {
  const notificationTrackingJson = localStorage.getItem(
    SCHEDULED_NOTIFICATION_KEY
  )

  let notificationTracking: ScheduledNotificationType = {}
  if (notificationTrackingJson) {
    try {
      notificationTracking = JSON.parse(
        notificationTrackingJson
      ) as ScheduledNotificationType
    } catch {
      // On parsing error, ignore localStorage value and use the new object
    }
  }
  return notificationTracking
}

export const updateScheduledOrdersNotificationTracking = (
  scheduledOrders: ActiveOrder[]
) => {
  const now = Date.now()
  const notificationTracking = getScheduledNotificationTracking()
  const updatedTracking: ScheduledNotificationType = {}
  let shouldNotify = false

  scheduledOrders.forEach(({ number, delivery }) => {
    const startDate = delivery.deliveryDate?.startDate
    if (!startDate) return

    const deliveryStart = addTimeZone(startDate).getTime()
    const timeDiff = now - deliveryStart
    const range = getTimeRangeFromStartDate(timeDiff)

    if (range) {
      updatedTracking[number] = range
      const orderInTracking = notificationTracking[number]
      if (!orderInTracking || (orderInTracking && orderInTracking !== range)) {
        shouldNotify = true
      }
    }
  })

  localStorage.setItem(
    SCHEDULED_NOTIFICATION_KEY,
    JSON.stringify(updatedTracking)
  )

  return shouldNotify
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const unwrapGraphqlData = (
  raw: Record<string, unknown>
): Record<string, unknown> => {
  const inner = raw.data
  if (isPlainRecord(inner)) {
    return inner
  }
  return raw
}

export const normalizeSellerNewOrderNotificationsPayload = (
  raw: unknown
): SellerNewOrderNotificationEntry[] => {
  if (!isPlainRecord(raw)) {
    return []
  }

  const root = unwrapGraphqlData(raw)

  const notificationsFlat = root.newOrderNotifications
  const notificationsNested = isPlainRecord(root.sellerNewOrderNotification)
    ? root.sellerNewOrderNotification.newOrderNotifications
    : undefined

  const notificationSource = notificationsFlat ?? notificationsNested
  if (Array.isArray(notificationSource) && notificationSource.length > 0) {
    return notificationSource
      .map((item): SellerNewOrderNotificationEntry | null => {
        if (!isPlainRecord(item)) {
          return null
        }
        const orderNumberRaw = item.orderNumber
        const deliveryTypeRaw = item.deliveryType
        if (
          orderNumberRaw === null ||
          (typeof orderNumberRaw !== 'string' &&
            typeof orderNumberRaw !== 'number')
        ) {
          return null
        }
        const orderNumber = String(orderNumberRaw)
        const deliveryType =
          deliveryTypeRaw === undefined || deliveryTypeRaw === null
            ? ''
            : typeof deliveryTypeRaw === 'string' ||
                typeof deliveryTypeRaw === 'number'
              ? String(deliveryTypeRaw)
              : ''
        return {
          orderNumber,
          deliveryType,
        }
      })
      .filter((x): x is SellerNewOrderNotificationEntry => x !== null)
  }

  const legacyFlat = root.newOrderNumbers
  const legacyNested = isPlainRecord(root.sellerNewOrderNotification)
    ? root.sellerNewOrderNotification.newOrderNumbers
    : undefined
  const legacySource = legacyFlat ?? legacyNested
  if (!Array.isArray(legacySource)) {
    return []
  }

  return legacySource.map(n => ({
    orderNumber: String(n),
    deliveryType: '',
  }))
}

export const normalizeSellerNewOrderNotificationResponse = (
  raw: unknown
): string[] => {
  const list = normalizeSellerNewOrderNotificationsPayload(raw)
  return list.map(({ orderNumber }) => orderNumber)
}
